import {
  Box,
  CircularProgress,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { NAVBAR_HEIGHT } from "../../../components/Navbar/NavDesktop";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import { useTranslation } from "../../../context/TranslationContext";
import { useAuth } from "../../../context/AuthContext";
import {
  getAllPagesForPortal,
  setVisiblePageForPortal,
} from "../../../services/api/apiClient";
import { Page } from "../../../services/api/types/portalConfiguration/portal";
import { extractErrorMessage } from "../../../utils/errorUtils";

export const PortalPages = () => {
  const { showToast } = useToast();
  const { portals, fetchPortals } = usePortal();
  const { auth } = useAuth();
  const { translations } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [loadingPageUid, setLoadingPageUid] = useState<string | null>(null);
  const portalPagesTranslations = translations.translations["portal-pages"];

  useEffect(() => {
    if (!portals.selectedPortal) return;

    const fetchPages = async () => {
      const portalUid = portals.selectedPortal?.portalUid;
      if (!portalUid) return;

      try {
        setLoading(true);
        const response = await getAllPagesForPortal(portalUid);
        setPages(response);
      } catch (error: any) {
        console.log("error in portal pages", error);
        showToast(
          extractErrorMessage(error, "Failed to fetch portal pages"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPages();
  }, [portals.selectedPortal, showToast]);

  const handleSwitchChange = async (page: Page) => {
    if (!portals.selectedPortal || !auth.user) return;

    const updatedVisibility = !page.isVisible;
    try {
      setLoadingPageUid(page.portalPageUid);
      await setVisiblePageForPortal(portals.selectedPortal.portalUid, {
        isVisible: updatedVisibility,
        pageId: page.portalPageUid,
        user: auth.user.UserPortalUid,
      });
      setPages((prev) =>
        prev.map((p) =>
          p.portalPageUid === page.portalPageUid
            ? { ...p, isVisible: updatedVisibility }
            : p,
        ),
      );
      await fetchPortals();
    } catch (error: any) {
      showToast(error.response?.data || "Failed to update page", "error");
    } finally {
      setLoadingPageUid(null);
    }
  };

  usePageMetadata(
    {
      title: portalPagesTranslations?.header?.portalPagesText || "Portal pages",
      breadcrumbs: [
        { label: "Portal configuration", path: `/portal-configuration` },
        { label: "Portal pages" },
      ],
    },
    [portalPagesTranslations],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: `calc(100vh - ${NAVBAR_HEIGHT})`,
        }}
      >
        <CircularProgress size={100} />
      </Box>
    );
  }

  const displayPages = pages.filter((page) => page.portalPageUid !== "GPC");

  const readOnlyPagePaths = ["/home", "/login", "/profile"];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 2,
      }}
    >
      {displayPages.map((page) => {
        const isReadOnly = readOnlyPagePaths.includes(page.path);
        return (
          <Paper key={page.portalPageUid} sx={{ py: 2.5, px: 3 }}>
            <Box>
              <Typography variant="h6">{page.name}</Typography>
              <Typography fontSize="14px">
                {portalPagesTranslations.pages?.visibleDsc}
              </Typography>
            </Box>
            {loadingPageUid === page.portalPageUid ? (
              <CircularProgress />
            ) : (
              <Switch
                sx={{
                  mt: 3,
                }}
                checked={page.isVisible}
                onChange={() => handleSwitchChange(page)}
                disabled={isReadOnly}
              />
            )}
          </Paper>
        );
      })}
    </Box>
  );
};
